import { API_BASE_URL } from '@/config/serverConfig';

function triggerDownload(href: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.rel = 'noopener noreferrer';
  a.target = '_blank';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Сохранить файл/изображение по URL (blob/data или fetch + object URL). */
export async function saveMessengerAttachment(url: string, fileName: string): Promise<boolean> {
  const name = fileName.trim() || 'download';
  try {
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      triggerDownload(url, name);
      return true;
    }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, name);
      URL.revokeObjectURL(objectUrl);
      return true;
    } catch {
      triggerDownload(url, name);
      return true;
    }
  } catch {
    return false;
  }
}

async function blobToPngBlob(blob: Blob): Promise<Blob | null> {
  if (blob.type === 'image/png') return blob;

  try {
    const bitmap = await createImageBitmap(blob);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      return await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png');
      });
    } finally {
      bitmap.close();
    }
  } catch {
    return null;
  }
}

async function writeImageBlobToClipboard(blob: Blob): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.write) return false;

  const pngBlob = await blobToPngBlob(blob);
  if (!pngBlob) return false;

  try {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
    return true;
  } catch {
    return false;
  }
}

async function copyFromResponse(res: Response): Promise<boolean> {
  if (!res.ok) return false;
  return writeImageBlobToClipboard(await res.blob());
}

/** Копировать изображение в буфер обмена (API proxy для http(s), fetch для blob/data). */
export async function copyMessengerImageToClipboard(url: string): Promise<boolean> {
  try {
    const trimmed = url.trim();
    if (!trimmed) return false;

    if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
      return copyFromResponse(await fetch(trimmed));
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const params = new URLSearchParams({ url: trimmed });
      return copyFromResponse(
        await fetch(`${API_BASE_URL}/messenger/files/content?${params.toString()}`, {
          credentials: 'include',
        }),
      );
    }

    return false;
  } catch {
    return false;
  }
}
